(() => {
  const SELECTOR = '[data-panchali-pdp]';

  class PanchaliProductPage {
    constructor(root) {
      if (root.dataset.pdpInitialized === 'true') return;
      root.dataset.pdpInitialized = 'true';
      this.root = root;
      this.form = root.querySelector('[data-product-form]');
      this.variantInput = root.querySelector('[data-pdp-variant-id]');
      this.quantityInput = root.querySelector('[data-pdp-quantity-input]');
      this.quantityOutput = root.querySelector('[data-pdp-quantity-output]');
      this.status = root.querySelector('[data-pdp-status]');
      this.variants = this.readJson('[data-pdp-variants]');
      this.quantity = Number(this.quantityInput?.value || 1);

      this.initGallery();
      this.initStickyPanel();
      this.initDescription();
      this.initPincode();
      this.initQuantity();
      this.initVariants();
      this.initPurchaseActions();
      this.initQuietActions();
      this.initDialogs();
      this.initFloatingVideo();
    }

    readJson(selector) {
      const script = this.root.querySelector(selector);
      if (!script) return [];
      try {
        return JSON.parse(script.textContent || '[]');
      } catch (error) {
        console.warn('Panchali product data could not be read.', error);
        return [];
      }
    }

    announce(message) {
      if (!this.status || !message) return;
      this.status.textContent = '';
      window.requestAnimationFrame(() => {
        this.status.textContent = message;
      });
    }

    initGallery() {
      const track = this.root.querySelector('[data-pdp-gallery-track]');
      const slides = Array.from(this.root.querySelectorAll('[data-gallery-index]'));
      const thumbs = Array.from(this.root.querySelectorAll('[data-pdp-gallery-thumb]'));
      const counter = this.root.querySelector('[data-pdp-gallery-count]');
      if (!track || !slides.length) return;

      const activate = (index, shouldScroll = false) => {
        const safeIndex = Math.max(0, Math.min(slides.length - 1, index));
        slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === safeIndex));
        thumbs.forEach((thumb, thumbIndex) => {
          if (thumbIndex === safeIndex) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });
        if (counter) counter.textContent = `${safeIndex + 1} / ${slides.length}`;
        if (shouldScroll && window.matchMedia('(max-width: 767.98px)').matches) {
          track.scrollTo({ left: slides[safeIndex].offsetLeft, behavior: this.reducedMotion() ? 'auto' : 'smooth' });
        }
      };

      this.activateGallery = activate;
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => activate(Number(thumb.dataset.pdpGalleryThumb), true));
      });

      let ticking = false;
      track.addEventListener('scroll', () => {
        if (ticking || !window.matchMedia('(max-width: 767.98px)').matches) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          const width = track.clientWidth || 1;
          activate(Math.round(track.scrollLeft / width));
          ticking = false;
        });
      }, { passive: true });
    }

    initStickyPanel() {
      const panel = this.root.querySelector('[data-pdp-panel]');
      const inner = panel?.querySelector('.panchali-pdp__panel-inner');
      if (!inner || !window.ResizeObserver) return;
      const update = () => {
        const breathingRoom = 24;
        const top = Math.min(breathingRoom, window.innerHeight - inner.offsetHeight - breathingRoom);
        panel.style.setProperty('--pp-panel-top', `${Math.round(top)}px`);
      };
      update();
      this.panelObserver = new ResizeObserver(update);
      this.panelObserver.observe(inner);
      window.addEventListener('resize', update, { passive: true });
    }

    initDescription() {
      const content = this.root.querySelector('[data-pdp-description-content]');
      const toggle = this.root.querySelector('[data-pdp-description-toggle]');
      if (!content || !toggle) return;
      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!expanded));
        content.toggleAttribute('data-expanded', !expanded);
        toggle.textContent = expanded ? toggle.dataset.readMore : toggle.dataset.readLess;
      });
    }

    initPincode() {
      const input = this.root.querySelector('[data-pdp-pincode-input]');
      const button = this.root.querySelector('[data-pdp-pincode-check]');
      const message = this.root.querySelector('[data-pdp-pincode-message]');
      if (!input || !button || !message) return;
      const check = () => {
        input.value = input.value.replace(/\D/g, '').slice(0, 6);
        const valid = /^\d{6}$/.test(input.value);
        message.hidden = false;
        message.textContent = valid ? this.root.dataset.pincodeSuccess : this.root.dataset.pincodeError;
        message.dataset.state = valid ? 'success' : 'error';
        if (!valid) input.focus();
      };
      input.addEventListener('input', () => {
        input.value = input.value.replace(/\D/g, '').slice(0, 6);
        message.hidden = true;
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          check();
        }
      });
      button.addEventListener('click', check);
    }

    initQuantity() {
      const minus = this.root.querySelector('[data-pdp-quantity-minus]');
      const plus = this.root.querySelector('[data-pdp-quantity-plus]');
      const setQuantity = (value) => {
        this.quantity = Math.max(1, Math.min(10, Number(value) || 1));
        if (this.quantityInput) this.quantityInput.value = String(this.quantity);
        if (this.quantityOutput) this.quantityOutput.textContent = String(this.quantity);
        if (minus) minus.disabled = this.quantity <= 1;
        if (plus) plus.disabled = this.quantity >= 10;
      };
      minus?.addEventListener('click', () => setQuantity(this.quantity - 1));
      plus?.addEventListener('click', () => setQuantity(this.quantity + 1));
      setQuantity(this.quantity);
    }

    initVariants() {
      const optionSelectors = Array.from(this.root.querySelectorAll('[data-pdp-option-index]'));
      if (!optionSelectors.length || !this.variants.length) return;
      const update = () => {
        const selected = optionSelectors.map((select) => select.value);
        const variant = this.variants.find((item) => item.options.every((value, index) => value === selected[index]));
        if (!variant) return;
        this.updateVariant(variant);
      };
      optionSelectors.forEach((select) => select.addEventListener('change', update));
    }

    updateVariant(variant) {
      if (this.variantInput) this.variantInput.value = String(variant.id);
      const price = this.root.querySelector('[data-pdp-price]');
      const compare = this.root.querySelector('[data-pdp-compare-price]');
      if (price) price.textContent = variant.price;
      if (compare) {
        compare.textContent = variant.compareAt || '';
        compare.classList.toggle('is-hidden', !variant.compareAt);
      }

      const purchaseButtons = this.root.querySelectorAll('[data-pdp-add], [data-pdp-buy-now], [data-pdp-sticky-add], [data-pdp-sticky-buy]');
      purchaseButtons.forEach((button) => { button.disabled = !variant.available; });
      const addLabels = this.root.querySelectorAll('[data-pdp-add-label], [data-pdp-sticky-add]');
      addLabels.forEach((label) => { label.textContent = variant.available ? this.root.dataset.addLabel : this.root.dataset.soldOutLabel; });

      if (variant.mediaId) {
        const media = this.root.querySelector(`[data-media-id="${variant.mediaId}"]`);
        if (media && this.activateGallery) this.activateGallery(Number(media.dataset.galleryIndex), true);
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    initPurchaseActions() {
      const add = this.root.querySelector('[data-pdp-add]');
      const buy = this.root.querySelector('[data-pdp-buy-now]');
      const stickyAdd = this.root.querySelector('[data-pdp-sticky-add]');
      const stickyBuy = this.root.querySelector('[data-pdp-sticky-buy]');

      stickyAdd?.addEventListener('click', () => add?.click());
      stickyBuy?.addEventListener('click', () => buy?.click());

      buy?.addEventListener('click', async () => {
        if (!this.form || buy.disabled) return;
        const allBuyButtons = [buy, stickyBuy].filter(Boolean);
        allBuyButtons.forEach((button) => { button.disabled = true; });
        try {
          const base = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${base}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(this.form),
          });
          if (!response.ok) throw new Error('Add to cart failed');
          window.location.assign(`${base}checkout`);
        } catch (error) {
          console.warn(error);
          this.announce(this.root.dataset.addError);
          allBuyButtons.forEach((button) => { button.disabled = false; });
        }
      });
    }

    initQuietActions() {
      const save = this.root.querySelector('[data-pdp-save]');
      const share = this.root.querySelector('[data-pdp-share]');
      const storageKey = `panchali:saved:${this.root.dataset.productHandle}`;
      try {
        save?.setAttribute('aria-pressed', String(window.localStorage.getItem(storageKey) === 'true'));
      } catch (_) {}

      save?.addEventListener('click', () => {
        const active = save.getAttribute('aria-pressed') === 'true';
        save.setAttribute('aria-pressed', String(!active));
        try { window.localStorage.setItem(storageKey, String(!active)); } catch (_) {}
        this.announce(active ? this.root.dataset.removedMessage : this.root.dataset.savedMessage);
      });

      share?.addEventListener('click', async () => {
        const data = { title: document.title, url: window.location.href };
        if (navigator.share) {
          try { await navigator.share(data); return; } catch (error) {
            if (error?.name === 'AbortError') return;
          }
        }
        try {
          await navigator.clipboard.writeText(window.location.href);
          this.announce(this.root.dataset.copiedMessage);
        } catch (_) {}
      });
    }

    initDialogs() {
      const lightbox = this.root.querySelector('[data-pdp-lightbox]');
      const lightboxImage = lightbox?.querySelector('[data-pdp-lightbox-image]');
      this.root.querySelectorAll('[data-pdp-lightbox-open]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!lightbox || !lightboxImage) return;
          lightboxImage.src = button.dataset.fullSrc;
          lightboxImage.alt = button.dataset.fullAlt || '';
          lightbox.showModal();
        });
      });

      this.root.querySelectorAll('dialog').forEach((dialog) => {
        dialog.querySelectorAll('[data-pdp-dialog-close]').forEach((button) => {
          button.addEventListener('click', () => dialog.close());
        });
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        dialog.addEventListener('close', () => {
          dialog.querySelectorAll('video').forEach((video) => video.pause());
        });
      });
    }

    initFloatingVideo() {
      const card = this.root.querySelector('[data-pdp-floating-video]');
      const video = card?.querySelector('video');
      if (!card || !video) return;
      const close = card.querySelector('[data-pdp-floating-close]');
      const toggle = card.querySelector('[data-pdp-floating-toggle]');
      const watch = card.querySelector('[data-pdp-floating-watch]');
      const dialog = this.root.querySelector('[data-pdp-video-dialog]');
      const dialogVideo = dialog?.querySelector('video');
      const playIcon = card.querySelector('[data-pdp-play-icon]');
      const pauseIcon = card.querySelector('[data-pdp-pause-icon]');
      const storageKey = `panchali:floating-video:${this.root.dataset.productHandle}`;

      try {
        if (window.sessionStorage.getItem(storageKey) === 'closed') card.hidden = true;
      } catch (_) {}

      const sync = () => {
        const playing = !video.paused;
        if (playIcon) playIcon.hidden = playing;
        if (pauseIcon) pauseIcon.hidden = !playing;
        if (toggle) toggle.setAttribute('aria-label', playing ? this.root.dataset.pauseLabel : this.root.dataset.playLabel);
      };
      video.muted = true;
      video.play().catch(sync);
      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);
      sync();

      toggle?.addEventListener('click', () => {
        if (video.paused) video.play().catch(sync);
        else video.pause();
      });
      close?.addEventListener('click', () => {
        video.pause();
        card.hidden = true;
        try { window.sessionStorage.setItem(storageKey, 'closed'); } catch (_) {}
      });
      watch?.addEventListener('click', () => {
        video.pause();
        if (!dialog || !dialogVideo) return;
        dialog.showModal();
        dialogVideo.currentTime = video.currentTime || 0;
        dialogVideo.play().catch(() => {});
      });
      dialog?.addEventListener('close', () => {
        dialogVideo?.pause();
        if (!card.hidden) video.play().catch(sync);
      });
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  const initialize = (scope = document) => {
    const roots = scope.matches?.(SELECTOR) ? [scope] : Array.from(scope.querySelectorAll?.(SELECTOR) || []);
    roots.forEach((root) => new PanchaliProductPage(root));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
